import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Icon, Intent, MenuItem } from "@blueprintjs/core";
import classNames from "classnames";
import { Button } from "components/buttons";
import { AlertConfirm } from "components/notifications";
import { useValidation } from "hooks/useValidation";
import { FormReadonly, FormSwitch, FormTextInput } from "components/form-fields";
import { GridLayout } from "components/grid";
import { HierarchyRepeatableModal } from "components/hierarchy/HierarchyRepeatableModal";
import { HierarchyConditionalModal } from "components/hierarchy/HierarchyConditionalModal";
import { HierarchyAddQuestionModal } from "components/hierarchy/HierarchyAddQuestionModal";
import { HierarchyConditionsHelper } from "components/hierarchy/HierarchyConditionsHelper";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from '@dnd-kit/utilities';
import { useDirtyData } from "hooks/useDirtyData";
import { Dropdown } from "components/dropdown";
import './Hierarchy.css';

/**
 * HierarchySection component for displaying hierarchical sections with the ability to edit, clone, delete
 * and add child sections or questions.
 */
export function HierarchySection(
  {
    disabled = false,
    loading = false,
    hideAddComponentButtons = false,
    children,
    conditionalHelperText,
    currentTriggerQuestion,
    inEditMode,
    isItemBeingMoved,
    isPage,
    onClear,
    onClone,
    onDelete,
    onEdit,
    onPageAdd,
    onQuestionAdd,
    onSave,
    onSectionAdd,
    pottentialTriggerQuestions,
    questionTypes,
    readOnly,
    reasonWhyComponentCannotBeRepeatable,
    section
  }
) {
  const [expanded, setExpanded] = useState(true);
  const [wasExpandedBefore, setWasExpandedBefore] = useState(true);
  const [isValid, errors, validate] = useValidation();
  const sectionTypeName = useMemo(() => isPage ? "Page" : "Section", [isPage]);
  const [helperTextWarning, setHelperTextWarning] = useState('');
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [settingRepeatability, setSettingRepeatability] = useState(false);
  const [settingConditionality, setSettingConditionality] = useState(false);
  const [showClearSettingsWarning, setShowClearSettingsWarning] = useState(false);
  const [isNew, setIsNew] = useState(false);

  // Store all section settings in internal state
  const [settings, setSettings] = useState({
    id: section.id,
    title: section.title,
    helperText: '',
    componentNumber: section.componentNumber,
    condition: null,
    repeatability: null,
    recordLocation: false,
    recordTimestamp: false
  });

  const [oldSettings, setOldSettings] = useState({});

  const triggerQuestion = useMemo(() => HierarchyConditionsHelper.getTriggerQuestion(settings.condition, pottentialTriggerQuestions)
    , [settings.condition, pottentialTriggerQuestions]);

  const triggerAnswers = useMemo(() => HierarchyConditionsHelper.getConcatenatedAnswers(triggerQuestion, settings.condition),
    [triggerQuestion, settings.condition, pottentialTriggerQuestions]);

  const isDataDirty = useDirtyData(oldSettings, settings);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `section-${settings.id}`,
    disabled: readOnly
  });

  const draggableStyle = {
    transform: CSS.Translate.toString(transform),
  };

  useEffect(() => {
    if (isItemBeingMoved) {
      setWasExpandedBefore(expanded);
      setExpanded(false);
    } else {
      setExpanded(wasExpandedBefore);
    }
  }, [isItemBeingMoved]);

  // Update settings if section props change from parent
  useEffect(() => {
    setIsNew(!!section.isNew);
    setSettings(section);
    setOldSettings({ ...section });
  }, [section]);

  // Validate settings and update validation state
  useEffect(() => {
    let rules = [];

    if (isPage) {
      rules.push({ fieldName: "title", required: true });
    }

    if (!isPage && !settings.title && settings.helperText) {
      setHelperTextWarning('Helper text can only be set for sections with a title.');
    } else {
      setHelperTextWarning('');
    }

    validate(rules, settings);
  }, [settings, isPage]);


  useEffect(() => {
    if (inEditMode) {
      setExpanded(true);
    }
  }, [inEditMode]);

  // Handle keyboard interactions for interactive elements
  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action(e);
    }
  };

  // Handle settings change in edit mode
  const handleSettingsChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value,
    });
  };

  // Handle repeatable toggle
  const handleRepeatableToggle = () => {
    setSettingRepeatability(true);
  };

  // Handle conditional toggle
  const handleConditionalToggle = () => {
    setSettingConditionality(true);
  };

  // Save changes and exit edit mode
  const handleSave = () => {
    onSave(section.id, { ...settings, isNew: false });
  };

  // Handle section edit click
  const handleEdit = () => {
    onEdit(section.id);
  };

  // Handle section duplication
  const handleClone = () => {
    onClone(section.id);
  };

  // Handle section deletion
  const handleDelete = () => {
    onDelete(section.id);
  };

  // Handle toggle of expanded/collapsed state
  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Handle adding a new section
  const handleAddSection = (e, addInnerSection = true) => {
    setExpanded(addInnerSection);
    onSectionAdd(addInnerSection);
  };

  // Handle adding a new question
  const addInnerQuestion = useRef(true);
  const handleAddQuestion = (e, addInnerSection = true) => {
    addInnerQuestion.current = addInnerSection;
    setExpanded(addInnerSection);
    setAddingQuestion(true);
  };

  const handleAddedQuestion = (addedQuestionType) => {
    onQuestionAdd(addedQuestionType, addInnerQuestion.current);
    setAddingQuestion(false);
  }

  // Handle adding a new section with keyboard support
  const handleAddSectionKeyDown = (e) => {
    handleKeyDown(e, handleAddSection);
  };

  // Handle adding a new question with keyboard support
  const handleAddQuestionKeyDown = (e) => {
    handleKeyDown(e, handleAddQuestion);
  };

  const onRepeatabilityChange = (newValue) => {
    setSettings({
      ...settings,
      repeatability: newValue ? { ...newValue } : null
    });
    setSettingRepeatability(false);
  };

  const onConditionalityChange = (newValue) => {
    setSettings({
      ...settings,
      condition: newValue ? { ...newValue } : null
    });
    setSettingConditionality(false);
  };

  const handleClear = () => {
    if (isDataDirty) {
      setShowClearSettingsWarning(true);
    } else {
      onClear();
    }
  }

  const onClearConfirmed = () => {
    setShowClearSettingsWarning(false);
    onClear();
  }

  // Render title section - either as editable input or static text
  const renderTitle = () => {
    return (
      <div className="inline-items text-truncate">
        <ShowHide
          evaluator={readOnly}
          hide={<Icon {...listeners} {...attributes} className='drag-handle' icon="layout-grid" />}
        />
        <h4 className="hierarchy-title text-truncate">
          {section.title ? section.title : "Untitled Section"}
        </h4>
      </div>
    );
  };

  // Action buttons for the section header
  const renderActionButtons = () => {

    const inEditModeValue = inEditMode;

    if (readOnly) {
      return (
        <div className="action-button-group">
          <Tooltip content={inEditModeValue ? "Close" : "View"}>
            <Button
              icon={inEditModeValue ? "cross" : "eye-open"}
              intent={inEditModeValue ? Intent.PRIMARY : ""}
              minimal={!inEditModeValue}
              large={false}
              onClick={inEditModeValue ? handleSave : handleEdit}
              loading={loading}
              disabled={
                disabled || (inEditModeValue && (!isValid || helperTextWarning))
              }
              className="spacer-right-small"
              iconOnly
            />
          </Tooltip>
          {renderExpandButton()}
        </div>
      );
    }

    const menuItems = [];

    menuItems.push({ key: 'duplicate', icon: "duplicate", text: "Duplicate", onClick: handleClone, disabled: disabled || inEditModeValue });

    menuItems.push({ key: "add-question", icon: "add", text: "Add Question", minimal: true, large: false, onClick: (e) => handleAddQuestion(e, isPage), disabled: disabled || inEditModeValue });
    menuItems.push({ key: "add-section", icon: "", text: "Add Section", minimal: true, large: false, onClick: (e) => handleAddSection(e, isPage), disabled: disabled || inEditModeValue });
    menuItems.push({ key: "add-page", icon: "", text: "Add Page", minimal: true, large: false, onClick: onPageAdd, disabled: disabled || inEditModeValue });

    return (
      <div className="action-button-group">
        <Tooltip content={inEditMode ? "Save" : "Edit"}>
          <Button
            icon={inEditMode ? "floppy-disk" : "edit"}
            intent={inEditMode ? Intent.PRIMARY : ""}
            minimal={!inEditMode}
            large={false}
            onClick={inEditMode ? handleSave : handleEdit}
            loading={loading}
            disabled={
              disabled || (inEditMode && (!isValid || helperTextWarning))
            }
            className="spacer-right-small"
            iconOnly
          />
        </Tooltip>
        <ShowHide
          evaluator={inEditMode && !isNew}
          show={<Tooltip content={isDataDirty ? "Clear" : "Close"}>
            <Button
              icon="cross"
              minimal={true}
              large={false}
              onClick={handleClear}
              loading={loading}
              disabled={disabled}
            />
          </Tooltip>}
        />
        <Tooltip content="Delete">
          <Button
            icon="trash"
            minimal={true}
            large={false}
            onClick={handleDelete}
            loading={loading}
            disabled={disabled}
            className="spacer-right-small bp6-intent-danger"
          />
        </Tooltip>
        <ShowHide
          evaluator={inEditMode}
          hide={
            <Dropdown
              icon='menu'
              showArrow={false}
              loading={loading}
              items={menuItems.map(i => <MenuItem {...i} />)}
              className='hierarchy-dropdown-menu'
            />
          }
        />
        {renderExpandButton()}
      </div>
    );
  };

  const renderExpandButton = () => {
    return <>
      <div className="hierarchy-title-row-divide"></div>
      <Button
        icon={expanded ? "chevron-up" : "chevron-down"}
        minimal={true}
        large={false}
        onClick={handleToggleExpand}
        loading={loading}
        className="spacer-right-small"
      />
    </>
  }

  // Add controls - buttons for adding sections and questions
  const renderAddControls = () => {
    if (readOnly) {
      return <></>
    }
    return (
      <div className="spacer-top spacer-bottom">
        <div className="padded info-panel-content">
          <div className="inline-items">
            <div className="inline-item fill">
              <button
                className={classNames("hierarchy-drop notification-panel hierarchy-add-child", { 'bp6-skeleton': loading })}
                onClick={handleAddSection}
                onKeyDown={handleAddSectionKeyDown}
                tabIndex={0}
                aria-label="Add section"
                type="button"
                disabled={disabled || inEditMode}
              >
                <span>ADD SECTION</span>
              </button>
            </div>
            <div className="inline-item fill">
              <button
                className={classNames("hierarchy-drop notification-panel hierarchy-add-child", { 'bp6-skeleton': loading })}
                onClick={handleAddQuestion}
                onKeyDown={handleAddQuestionKeyDown}
                tabIndex={0}
                aria-label="Add question"
                type="button"
                disabled={disabled || inEditMode}
              >
                <span>ADD QUESTION</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main component layout
  return (
    <div className='hierarchy-item-draggable-container'>
      <div style={draggableStyle} ref={setNodeRef} className={classNames("hierarchy-section hierarchy-item", { "active": inEditMode, "disabled": disabled, "page": isPage, "being-moved": isItemBeingMoved })}>
        <div
          className={expanded ? "section-expanded" : "section-collapsed"}
        >
          <div className="hierarchy-title-row">
            {renderTitle()}
            <div className='inline-items hierarchy-title-text'>
              <div><span className='hierarchy-title-text-header'>{sectionTypeName}:</span> {settings.componentNumber}</div>

              <ShowHide
                evaluator={!!section.condition}
                show={<div>
                  <Tooltip content={`Conditional based on trigger question ${currentTriggerQuestion?.componentNumber}`}>
                    <span className='hierarchy-title-text-header with-icon'>Conditional <Icon icon="info-sign" size={12} /></span>
                  </Tooltip>
                </div>}
              />

              <ShowHide
                evaluator={!!section.repeatability}
                show={<div>
                  <Tooltip content={`Repeatable between ${section.repeatability?.minimumNumberOfRepeats} to ${section.repeatability?.maximumNumberOfRepeats} times`}>
                    <span className='hierarchy-title-text-header with-icon'>Repeatable <Icon icon="info-sign" size={12} /></span>
                  </Tooltip>
                </div>}
              />

              <div className='inline-items'>
                {renderActionButtons()}
              </div>
            </div>
          </div>

          {inEditMode && (
            <div className="section-content">
              <ShowHide
                evaluator={readOnly}
                show={<>
                  <FormReadonly
                    value={settings.title ?? "Untitled"}
                    headingText={`${sectionTypeName} header`}
                  />
                  <FormReadonly
                    value={settings.helperText ? settings.helperText : "-"}
                    headingText={`${sectionTypeName} helper text`}
                  />
                </>}
                hide={<>
                  <FormTextInput
                    value={settings.title}
                    onChange={(e) => handleSettingsChange("title", e.target.value)}
                    placeholder="Please type the header here..."
                    dangerHelperText={errors.title}
                    disabled={disabled}
                    fill
                    headingText={`${sectionTypeName} Header`}
                  />
                  <FormTextInput
                    value={settings.helperText}
                    onChange={(e) => handleSettingsChange("helperText", e.target.value)}
                    placeholder="Please type the helper text here..."
                    dangerHelperText={errors.helperText || helperTextWarning}
                    disabled={disabled}
                    fill
                    headingText={`${sectionTypeName} helper text`}
                  />
                </>}
              />
              <h3>Section Options</h3>
              <div className='hierarchy-section-options'>
                <GridLayout numberOfColumns={4} className='hierarchy-question-grid'>
                  <FormSwitch
                    checked={settings.recordLocation}
                    onChange={(e) => handleSettingsChange("recordLocation", e.target.checked)}
                    disabled={readOnly}
                    headingText="Record location"
                    helperText="Save the user's location when this section is completed."
                  />
                  <FormSwitch
                    checked={settings.recordTimestamp}
                    onChange={(e) => handleSettingsChange("recordTimestamp", e.target.checked)}
                    disabled={readOnly}
                    headingText="Record time"
                    helperText="Save the date and time when this section is completed."
                  />
                  <div>
                    <Tooltip content={reasonWhyComponentCannotBeRepeatable} position="right">
                      <FormSwitch
                        checked={!!settings.repeatability}
                        onChange={handleRepeatableToggle}
                        disabled={!!reasonWhyComponentCannotBeRepeatable || readOnly}
                        headingText="Repeatable"
                        labelElement={settings.repeatability ? `Min ${settings.repeatability.minimumNumberOfRepeats}, Max ${settings.repeatability.maximumNumberOfRepeats}` : ''}
                      />
                    </Tooltip>
                  </div>
                  <div>
                    <Tooltip content={conditionalHelperText} position="right">
                      <FormSwitch
                        checked={!!settings.condition}
                        onChange={handleConditionalToggle}
                        disabled={!pottentialTriggerQuestions.length || readOnly}
                        headingText="Conditional"
                        labelElement={triggerQuestion ? `Trigger question ${triggerQuestion.componentNumber}, Answers: ${triggerAnswers}` : ''}
                      />
                    </Tooltip>
                  </div>
                </GridLayout>
              </div>
            </div>
          )}

          <div >
            <div className="hierarchy-section-container">
              {children}
              <ShowHide
                evaluator={readOnly && !inEditMode && !settings.childComponents?.length}
                show={<div className={classNames({ 'bp6-skeleton': loading })}>Section does not contain any components.</div>}
              />
              <ShowHide
                evaluator={!hideAddComponentButtons}
                show={renderAddControls()}
              />
            </div>
          </div>
        </div>

        <HierarchyAddQuestionModal
          isOpen={addingQuestion}
          questionTypes={questionTypes}
          onCancel={() => setAddingQuestion(false)}
          onSave={handleAddedQuestion}
        />

        <HierarchyRepeatableModal
          isOpen={settingRepeatability}
          component={settings}
          isSection={true}
          onCancel={() => setSettingRepeatability(false)}
          onSave={(newRepeatability) => onRepeatabilityChange(newRepeatability)}
        />
        <HierarchyConditionalModal
          isOpen={settingConditionality}
          component={settings}
          isSection={true}
          onCancel={() => setSettingConditionality(false)}
          onSave={(newCondition) => onConditionalityChange(newCondition)}
          pottentialTriggerQuestions={pottentialTriggerQuestions}
        />
      </div>
      <AlertConfirm
        title="Clear changes"
        isOpen={showClearSettingsWarning}
        onConfirm={onClearConfirmed}
        onCancel={() => setShowClearSettingsWarning(false)}
        confirmButtonIntent="danger"
        confirmButtonText="Clear changes"
      >
        <p>Are you sure you want to clear your changes? Any changes made to the {isPage ? "page" : "section"} will be removed.</p>
      </AlertConfirm>
    </div>
  );
}

HierarchySection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string.isRequired,
    helperText: PropTypes.string,
    componentNumber: PropTypes.number,
    repeatable: PropTypes.bool,
    conditional: PropTypes.bool,
    recordTimestamp: PropTypes.bool,
    recordLocation: PropTypes.bool
  }).isRequired,
  children: PropTypes.node,
  isPage: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClone: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPageAdd: PropTypes.func.isRequired,
  onSectionAdd: PropTypes.func.isRequired,
  onQuestionAdd: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  questionTypes: PropTypes.array.isRequired,
  inEditMode: PropTypes.bool.isRequired,
  reasonWhyComponentCannotBeRepeatable: PropTypes.string,
  currentTriggerQuestion: PropTypes.object,
  pottentialTriggerQuestions: PropTypes.array.isRequired,
  conditionalHelperText: PropTypes.node.isRequired,
  hideAddComponentButtons: PropTypes.bool,
  isItemBeingMoved: PropTypes.bool,
  readOnly: PropTypes.bool
};
